import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import type { ClusterPreviewResponse } from '../api/organizeGeneral';
import {
  classificationSourceLabel,
  confidenceBandLabel,
  formatConfidenceDistribution,
} from '../utils/confidenceBandDisplay';

type OrganizeGeneralClusterPreviewProps = {
  loading: boolean;
  preview: ClusterPreviewResponse | null;
  error: string | null;
  notFound: boolean;
  approving: boolean;
  approveError: string | null;
  rejecting: boolean;
  ignoring: boolean;
  decisionError: string | null;
  onClose: () => void;
  onRetry: () => void;
  onApprove: () => void;
  onReject: () => void;
  onIgnore: () => void;
};

const OrganizeGeneralClusterPreview: React.FC<OrganizeGeneralClusterPreviewProps> = ({
  loading,
  preview,
  error,
  notFound,
  approving,
  approveError,
  rejecting,
  ignoring,
  decisionError,
  onClose,
  onRetry,
  onApprove,
  onReject,
  onIgnore,
}) => {
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);

  if (loading) {
    return (
      <Box
        className="flex items-center gap-3 py-6"
        data-testid="ORGANIZE_GENERAL_CLUSTER_PREVIEW_LOADING"
        aria-live="polite"
      >
        <CircularProgress size={24} aria-label="Loading cluster preview" />
        <Typography variant="body2" color="text.secondary">
          Loading preview…
        </Typography>
      </Box>
    );
  }

  if (notFound) {
    return (
      <Alert
        severity="info"
        data-testid="ORGANIZE_GENERAL_CLUSTER_PREVIEW_NOT_FOUND"
        action={
          <Button color="inherit" size="small" onClick={onClose}>
            Close
          </Button>
        }
      >
        This suggestion is no longer available.
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        data-testid="ORGANIZE_GENERAL_CLUSTER_PREVIEW_ERROR"
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (!preview) {
    return null;
  }

  const distributionText = formatConfidenceDistribution(preview.confidence_band_distribution);
  const actionsDisabled =
    preview.status !== 'pending_review' || approving || rejecting || ignoring;

  const handleConfirmApprove = () => {
    setApproveConfirmOpen(false);
    onApprove();
  };

  const handleConfirmReject = () => {
    setRejectConfirmOpen(false);
    onReject();
  };

  return (
    <Box
      data-testid="ORGANIZE_GENERAL_CLUSTER_PREVIEW"
      className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
      aria-live="polite"
    >
      <Box className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <Box>
          <Typography variant="h6" component="h3">
            {preview.suggested_label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {preview.item_count} {preview.item_count === 1 ? 'item' : 'items'} in this group
          </Typography>
        </Box>
        <Button size="small" onClick={onClose} data-testid="ORGANIZE_GENERAL_CLUSTER_PREVIEW_CLOSE">
          Close preview
        </Button>
      </Box>

      <Box className="mb-4 space-y-2">
        <Typography variant="subtitle2" component="h4">
          Confidence
        </Typography>
        <Typography variant="body2">{preview.confidence_cue}</Typography>
        {distributionText ? (
          <Typography variant="body2" color="text.secondary">
            {distributionText}
          </Typography>
        ) : null}
      </Box>

      <Box className="mb-4 space-y-1">
        <Typography variant="subtitle2" component="h4">
          Grouping rationale
        </Typography>
        <Typography variant="body2">{preview.grouping_rationale_summary}</Typography>
      </Box>

      <Box className="mb-4 space-y-1">
        <Typography variant="subtitle2" component="h4">
          Manual corrections
        </Typography>
        <Typography variant="body2">{preview.override_impact.statement}</Typography>
      </Box>

      <Box className="mb-4">
        <Typography variant="subtitle2" component="h4" className="mb-2">
          Example items
        </Typography>
        {preview.example_items.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="ORGANIZE_GENERAL_CLUSTER_PREVIEW_NO_EXAMPLES"
          >
            No example items are available for this group right now.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" aria-label="Cluster example items">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Confidence</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Evidence</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.example_items.map((example) => (
                  <TableRow key={example.work_item_id}>
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/app/workbench?focusId=${encodeURIComponent(example.work_item_id)}&area=general`}
                        underline="hover"
                      >
                        {example.title}
                      </Link>
                    </TableCell>
                    <TableCell>{confidenceBandLabel(example.confidence_band)}</TableCell>
                    <TableCell>{classificationSourceLabel(example.classification_source)}</TableCell>
                    <TableCell>{example.evidence_summary || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {approveError ? (
        <Alert severity="error" className="mb-3" data-testid="ORGANIZE_GENERAL_CLUSTER_APPROVE_ERROR">
          {approveError}
        </Alert>
      ) : null}

      {decisionError ? (
        <Alert severity="error" className="mb-3" data-testid="ORGANIZE_GENERAL_CLUSTER_DECISION_ERROR">
          {decisionError}
        </Alert>
      ) : null}

      <Box className="flex flex-wrap items-center gap-2">
        <Button
          variant="contained"
          color="primary"
          disabled={actionsDisabled}
          onClick={() => setApproveConfirmOpen(true)}
          data-testid="ORGANIZE_GENERAL_CLUSTER_APPROVE"
        >
          {approving ? 'Approving…' : 'Approve as subdomain'}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          disabled={actionsDisabled}
          onClick={onIgnore}
          data-testid="ORGANIZE_GENERAL_CLUSTER_IGNORE"
        >
          {ignoring ? 'Dismissing…' : 'Ignore for now'}
        </Button>
        <Button
          variant="text"
          color="inherit"
          disabled={actionsDisabled}
          onClick={() => setRejectConfirmOpen(true)}
          data-testid="ORGANIZE_GENERAL_CLUSTER_REJECT"
        >
          {rejecting ? 'Rejecting…' : 'Reject'}
        </Button>
        {approving || rejecting || ignoring ? (
          <CircularProgress size={20} aria-label="Processing cluster decision" />
        ) : null}
      </Box>

      <Dialog
        open={approveConfirmOpen}
        onClose={() => setApproveConfirmOpen(false)}
        aria-labelledby="organize-general-approve-dialog-title"
      >
        <DialogTitle id="organize-general-approve-dialog-title">
          Approve as subdomain?
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" className="mb-2">
              Subdomain label: <strong>{preview.suggested_label}</strong>
            </Typography>
            <Typography variant="body2" className="mb-2">
              Items to assign: <strong>{preview.item_count}</strong>
            </Typography>
            <Typography variant="body2">{preview.override_impact.statement}</Typography>
            <Typography variant="body2" color="text.secondary" className="mt-2">
              Items stay in General domain. Only subdomain grouping is applied.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmApprove}
            data-testid="ORGANIZE_GENERAL_CLUSTER_APPROVE_CONFIRM"
          >
            Approve and apply
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rejectConfirmOpen}
        onClose={() => setRejectConfirmOpen(false)}
        aria-labelledby="organize-general-reject-dialog-title"
      >
        <DialogTitle id="organize-general-reject-dialog-title">
          Reject this grouping?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This grouping won&apos;t be suggested again unless items change.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={rejecting}
            onClick={handleConfirmReject}
            data-testid="ORGANIZE_GENERAL_CLUSTER_REJECT_CONFIRM"
          >
            Reject grouping
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganizeGeneralClusterPreview;
